import { Box, chakra } from '@chakra-ui/react';
import { IoRocket } from 'react-icons/io5';
import { useSelector } from 'react-redux';

import DashboardPanel from '../DashboardPanel';
import SetupStep from './SetupStep';
import useSetupProgress from './useSetupProgress';
import useSetupProgressHidden from './useSetupProgressHidden';
import { SETUP_GREEN } from './constants';

/**
 * (3145) "Let's get you set up!" — the onboarding milestones across the top of
 * the dashboard. Hidden once every step is done, or when the user hides it.
 *
 * @param {object} props
 * @param {object[]|null} props.services - dashboard services; null while unknown
 * @param {object[]} props.appAuths - dashboard app auths
 * @param {boolean} [props.companyApproved] - from /company/{id}
 * @param {(stepKey: string) => void} props.onStepAction - the current step was clicked
 * @param {(stepKey: string) => void} props.onLearnHow - a tooltip's Learn how button was clicked
 */
const SetupProgress = ({
    services,
    appAuths,
    companyApproved,
    onStepAction,
    onLearnHow,
}) => {
    const { currentCompany } = useSelector((state) => state.navigationLists);
    const { darkMode } = useSelector((state) => state.settings);
    const userEmail = useSelector((state) => state.login?.email);
    const currentScope = useSelector((state) => state.login?.currentScope);

    const { steps, currentStepIndex, isComplete, stepServices, isLoading } =
        useSetupProgress({
            companyId: currentCompany,
            services,
            appAuths,
            currentScope,
            companyApproved,
        });
    const [isHidden, hide] = useSetupProgressHidden(currentCompany, userEmail);

    if (isLoading || isComplete || isHidden) return null;

    // The line runs through the unbroken run of finished steps from the start.
    const firstPending = steps.findIndex((step) => !step.done);
    const fillPercent =
        (Math.max(0, firstPending - 1) / (steps.length - 1)) * 100;

    return (
        <DashboardPanel
            title="Let's get you set up!"
            icon={<IoRocket size={18} color={SETUP_GREEN} aria-hidden />}
            action={
                <chakra.button
                    type="button"
                    onClick={hide}
                    fontSize="xs"
                    fontWeight="semibold"
                    color={darkMode ? 'gray.400' : 'gray.500'}>
                    Hide
                </chakra.button>
            }
            mb={6}>
            <Box mt={6} mb={8} px={6}>
                <Box position="relative">
                    <Box
                        h="6px"
                        borderRadius="full"
                        bg={darkMode ? 'gray.600' : 'gray.200'}>
                        <Box
                            h="full"
                            borderRadius="full"
                            bg={SETUP_GREEN}
                            w={`${fillPercent}%`}
                            transition="width 0.3s"
                            data-testid="setup-progress-fill"
                        />
                    </Box>
                    {steps.map((step, index) => (
                        <SetupStep
                            key={step.key}
                            step={step}
                            index={index}
                            stepCount={steps.length}
                            isCurrent={index === currentStepIndex}
                            stepServices={stepServices}
                            darkMode={darkMode}
                            onAction={() => onStepAction(step.key)}
                            onLearnHow={() => onLearnHow(step.key)}
                        />
                    ))}
                </Box>
            </Box>
        </DashboardPanel>
    );
};

export default SetupProgress;
